import type { Element, Root } from 'hast';

/**
 * Attributes carrying the 1-based source lines a rendered block came from.
 *
 * Rendered markdown normally throws its source positions away: prose has no
 * gutter, so "the line this paragraph is on" stops existing the moment the
 * tree becomes DOM. Surfaces that need to point back at the file — the
 * filesystem preview's line comments — turn stamping on via
 * `MarkdownContent`'s `trackSourceLines` and read these attributes off the
 * nearest ancestor of whatever the user pointed at.
 */
export const SOURCE_LINE_ATTR = 'data-source-line';
export const SOURCE_END_LINE_ATTR = 'data-source-end-line';

/** Selector for the nearest rendered block that knows its source position. */
export const SOURCE_LINE_SELECTOR = `[${SOURCE_LINE_ATTR}]`;

/**
 * Rehype plugin that stamps {@link SOURCE_LINE_ATTR} /
 * {@link SOURCE_END_LINE_ATTR} onto every element the markdown pipeline
 * produced from source text.
 *
 * Stamping every element rather than only top-level blocks is what makes the
 * result precise: a list item, a table row, and a nested paragraph each carry
 * their own range, so `element.closest()` from a hovered node resolves to the
 * smallest block that contains it. Elements a rehype plugin synthesized (no
 * `position`) are skipped and simply inherit their parent's range through that
 * same lookup.
 *
 * The attributes only reach the DOM for elements whose styled override
 * forwards them — see `forwardedHtmlProps` in `html-attributes.ts`.
 */
export function rehypeSourceLines() {
  return function transform(tree: Root): void {
    stampChildren(tree);
  };
}

function stampChildren(node: Root | Element): void {
  for (const child of node.children) {
    if (child.type !== 'element') continue;
    const start = child.position?.start.line;
    if (start !== undefined) {
      child.properties = {
        ...child.properties,
        [SOURCE_LINE_ATTR]: start,
        [SOURCE_END_LINE_ATTR]: child.position?.end.line ?? start,
      };
    }
    stampChildren(child);
  }
}

/**
 * Pick the source-line attributes out of a markdown component's props, for an
 * override that renders an element the plain `forwardedHtmlProps` path does not
 * cover (the `pre` that collapses into a fenced code block's own shell).
 * Returns an empty object when tracking is off, so the caller can keep its
 * no-wrapper behavior.
 */
export function sourceLineProps(props: object): Record<string, unknown> {
  const source = props as Record<string, unknown>;
  const line = source[SOURCE_LINE_ATTR];
  if (line === undefined) return {};
  const end = source[SOURCE_END_LINE_ATTR];
  return end === undefined
    ? { [SOURCE_LINE_ATTR]: line }
    : { [SOURCE_LINE_ATTR]: line, [SOURCE_END_LINE_ATTR]: end };
}
